import { Subscribe } from '@react-rxjs/core'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { ConfigurableProductDetailsFragment } from '../../../graphql/magento'
import { useResultWithLeftEffect } from '../../../hooks/useResultWithLeftEffect'
import {
  findVariantInProductQuery,
  useQueryProductByUrlKeyVaryByAuth,
} from '../../../lib/products/m2-products-by-url-key'
import { reduceSet } from '../../../utils/collectionTools'
import { constNull } from '../../../utils/constant'
import { formatAsCurrency } from '../../../utils/priceTools'
import { pluckData } from '../../../utils/result'
import { StyledFunctionComponent } from '../../types'
import { TypographyShimmer } from '../../TypographyShimmer'

export interface ItemSelectorOptionLabelWithPriceProps {
  item: ConfigurableProductDetailsFragment
  label: string
  skus: ReadonlyArray<string>
}

const priceLabelStyles = css`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`

const PriceLabelValue = styled.div<{ $hasPrice: boolean }>`
  ${priceLabelStyles};
  height: 58px;
  padding-top: 23px;
  padding-bottom: 17px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.coolGray.cool300};
  ${({ $hasPrice }) =>
    !$hasPrice &&
    css`
      border-bottom: none;
    `};
`

export const PriceLabelPrice = styled.div`
  ${priceLabelStyles};
  height: 33px;
  font-size: 16px;
  font-weight: 700;
  color: #1f2024;
`

const PriceLabelBox = styled.div`
  width: 100%;
`

const minValueInSet = reduceSet<number, number | undefined>(
  undefined,
  (acc: number | undefined, price): number | undefined =>
    acc == null ? price : Math.min(acc, price),
)

const maxValueInSet = reduceSet<number, number | undefined>(
  undefined,
  (acc: number | undefined, price): number | undefined =>
    acc == null ? price : Math.max(acc, price),
)

/**
 * Display variant price when authentication state settled
 */
const ItemSelectorOptionPriceLabelReactive: StyledFunctionComponent<{
  item: ConfigurableProductDetailsFragment
  label: string
  skus: ReadonlyArray<string>
}> = ({ className, item, label, skus }) => {
  const productResult = useQueryProductByUrlKeyVaryByAuth(item.url_key ?? '')
  const [, product] = useResultWithLeftEffect(productResult, pluckData, constNull)
  const itemId = item.id

  // determine min and max price of variants identified by skus
  const { minPrice, maxPrice } = useMemo(() => {
    const variantsForAuth =
      itemId == null || product == null
        ? undefined
        : skus.map((sku) => findVariantInProductQuery(product, { itemId, sku }))

    const prices = variantsForAuth?.reduce((acc: Set<number>, variant) => {
      const price =
        variant?.product?.__typename === 'SimpleProduct' && 'price_tiers' in variant.product
          ? variant.product.price_tiers?.[0]?.final_price?.value ?? undefined
          : undefined
      if (price != null) {
        acc.add(price)
      }
      return acc
    }, new Set<number>())

    // NOTE: these prices will likely always be the same, but let's support a range just in case
    return {
      minPrice: prices
        ? formatAsCurrency({ amount: minValueInSet(prices) ?? 0, ignoreFalsyAmount: true })
        : undefined,
      maxPrice: prices
        ? formatAsCurrency({ amount: maxValueInSet(prices) ?? 0, ignoreFalsyAmount: true })
        : undefined,
    }
  }, [itemId, product, skus])

  return (
    <PriceLabelBox className={className}>
      <PriceLabelValue $hasPrice={!!minPrice}>{label}</PriceLabelValue>
      {minPrice ? (
        <PriceLabelPrice>
          {minPrice}
          {!!maxPrice && maxPrice !== minPrice && ` - ${maxPrice}`}
        </PriceLabelPrice>
      ) : null}
    </PriceLabelBox>
  )
}

/**
 * Loading component for selector option with price
 */
const ItemSelectorOptionLabelWithPriceSkeleton: React.FC<{ label: string }> = ({ label }) => (
  <PriceLabelBox>
    <PriceLabelValue $hasPrice={true}>{label}</PriceLabelValue>
    <PriceLabelPrice>
      <TypographyShimmer $width="4em" />
    </PriceLabelPrice>
  </PriceLabelBox>
)

export const ItemSelectorOptionLabel: React.FC<{ label: string }> = ({ label }) => (
  <PriceLabelValue $hasPrice={false}>{label}</PriceLabelValue>
)

export const ItemSelectorOptionLabelWithPrice: React.FC<ItemSelectorOptionLabelWithPriceProps> = ({
  item,
  label,
  skus,
}) => (
  <Subscribe fallback={<ItemSelectorOptionLabelWithPriceSkeleton label={label} />}>
    <ItemSelectorOptionPriceLabelReactive item={item} label={label} skus={skus} />
  </Subscribe>
)

export const ItemSelectorOptionLabelForPaymentTiming: React.FC<{
  label: string
  price: number
  priceAside?: string
}> = ({ label, price, priceAside }) => (
  <PriceLabelBox>
    <PriceLabelValue $hasPrice={true}>{label}</PriceLabelValue>
    <PriceLabelPrice>
      {formatAsCurrency({ amount: price, ignoreFalsyAmount: false })}
      {priceAside ? ` ${priceAside}` : ''}
    </PriceLabelPrice>
  </PriceLabelBox>
)
